import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import {
    DeleteCod,
    LazyLoadAllCods,
    LazyLoadMyCods,
    LazyLoadTasks,
    LoadAllCods,
    LoadMyCods,
    LoadTasks,
    SetFilter
} from './cod-overview.action';
import { catchError, filter, Observable, of, tap } from 'rxjs';
import { CodService } from '../../shared/services/cod.service';
import { Page, PagedMetadata } from '../../shared/models/page-metadata.model';
import { GeneralCod } from '../../cod/models/cod.model';

const PAGE_SIZE = 20;

interface CodOverviewStateModel {
    search: string;

    allCods: GeneralCod[];
    allCodsMetadata: PagedMetadata | null;
    isLoadingAllCods: boolean;
    isLazyLoadingAllCods: boolean;

    myCods: GeneralCod[];
    myCodsMetadata: PagedMetadata | null;
    isLoadingMyCods: boolean;
    isLazyLoadingMyCods: boolean;

    tasks: GeneralCod[];
    tasksMetadata: PagedMetadata | null;
    isLoadingTasks: boolean;
    isLazyLoadingTasks: boolean;

    filters: {
        createdBy: string[];
        assignedTo: string[];
        natureOfComplaint: string[];
        status: string[];
        region: string[];
        country: string[];
        financialAdjustment: string[];
        search?: string;
    };
}

@State<CodOverviewStateModel>({
    name: 'codOverview',
    defaults: {
        search: '',
        allCods: [],
        allCodsMetadata: null,
        isLoadingAllCods: false,
        isLazyLoadingAllCods: false,

        myCods: [],
        myCodsMetadata: null,
        isLoadingMyCods: false,
        isLazyLoadingMyCods: false,

        tasks: [],
        tasksMetadata: null,
        isLoadingTasks: false,
        isLazyLoadingTasks: false,

        filters: {
            createdBy: [],
            assignedTo: [],
            natureOfComplaint: [],
            status: [],
            region: [],
            country: [],
            financialAdjustment: [],
            search: undefined
        }
    }
})
@Injectable()
export class CodOverviewState {
    constructor(private codService: CodService, private store: Store) {}

    @Selector()
    static allCodsLoaded(state: CodOverviewStateModel): boolean {
        return !(state?.allCodsMetadata?.hasNextPage ?? true);
    }

    @Selector()
    static isLazyLoadingAllCods(state: CodOverviewStateModel): boolean {
        return state.isLazyLoadingAllCods;
    }

    @Selector()
    static isLoadingAllCods(state: CodOverviewStateModel): boolean {
        return state.isLoadingAllCods;
    }

    @Selector()
    static allCods(state: CodOverviewStateModel): GeneralCod[] {
        return state.allCods || [];
    }

    @Selector()
    static myCodsLoaded(state: CodOverviewStateModel): boolean {
        return !(state?.myCodsMetadata?.hasNextPage ?? true);
    }

    @Selector()
    static isLazyLoadingMyCods(state: CodOverviewStateModel): boolean {
        return state.isLazyLoadingMyCods;
    }

    @Selector()
    static isLoadingMyCods(state: CodOverviewStateModel): boolean {
        return state.isLoadingMyCods;
    }

    @Selector()
    static myCods(state: CodOverviewStateModel): GeneralCod[] {
        return state.myCods || [];
    }

    @Selector()
    static tasksLoaded(state: CodOverviewStateModel): boolean {
        return !(state?.tasksMetadata?.hasNextPage ?? true);
    }

    @Selector()
    static isLazyLoadingTasks(state: CodOverviewStateModel): boolean {
        return state.isLazyLoadingTasks;
    }

    @Selector()
    static isLoadingTasks(state: CodOverviewStateModel): boolean {
        return state.isLoadingTasks;
    }

    @Selector()
    static tasks(state: CodOverviewStateModel): GeneralCod[] {
        return state.tasks || [];
    }

    @Selector()
    static filters(state: CodOverviewStateModel): { [key: string]: string[] | string } {
        return state.filters;
    }

    @Action(LoadAllCods)
    loadCods(ctx: StateContext<CodOverviewStateModel>) {
        const state = ctx.getState();
        const filters = state.filters;

        ctx.patchState({ isLoadingAllCods: true });
        return this.codService.getAllCods(0, PAGE_SIZE, filters).pipe(
            catchError(() => {
                ctx.patchState({ isLoadingAllCods: false });
                return of(void 0);
            }),
            filter(response => !!response),
            tap(response =>
                ctx.patchState({
                    allCods: response.items.flat(),
                    allCodsMetadata: response.metadata,
                    isLazyLoadingAllCods: false,
                    isLoadingAllCods: false
                })
            )
        );
    }

    @Action(LazyLoadAllCods)
    lazyLoadCods(ctx: StateContext<CodOverviewStateModel>): Observable<Page<GeneralCod>> {
        const state = ctx.getState();
        const filters = state.filters;
        const page = Math.round(ctx.getState().allCods.length / PAGE_SIZE);
        ctx.patchState({ isLazyLoadingAllCods: true });

        return this.codService.getAllCods(page, PAGE_SIZE, filters).pipe(
            catchError(() => {
                ctx.patchState({ isLazyLoadingAllCods: false });
                return of({ items: [], metadata: {} } as unknown as Page<GeneralCod>);
            }),
            tap(result =>
                ctx.patchState({
                    allCods: state.allCods.concat(result.items.flat()),
                    allCodsMetadata: result.metadata,
                    isLazyLoadingAllCods: false
                })
            )
        );
    }

    @Action(LoadMyCods)
    loadMyCods(ctx: StateContext<CodOverviewStateModel>) {
        const state = ctx.getState();
        const filters = state.filters;

        ctx.patchState({ isLoadingMyCods: true });
        return this.codService.getMyCods(0, PAGE_SIZE, filters).pipe(
            catchError(() => {
                ctx.patchState({ isLoadingMyCods: false });
                return of(void 0);
            }),
            filter(response => !!response),
            tap(response =>
                ctx.patchState({
                    myCods: response.items.flat(),
                    myCodsMetadata: response.metadata,
                    isLazyLoadingMyCods: false,
                    isLoadingMyCods: false
                })
            )
        );
    }

    @Action(LazyLoadMyCods)
    lazyLoadMyCods(ctx: StateContext<CodOverviewStateModel>): Observable<Page<GeneralCod>> {
        const state = ctx.getState();
        const filters = state.filters;

        const page = Math.round(ctx.getState().myCods.length / PAGE_SIZE);
        ctx.patchState({ isLazyLoadingMyCods: true });

        return this.codService.getMyCods(page, PAGE_SIZE, filters).pipe(
            catchError(() => {
                ctx.patchState({ isLazyLoadingMyCods: false });
                return of({ items: [], metadata: {} } as unknown as Page<GeneralCod>);
            }),
            tap(result =>
                ctx.patchState({
                    myCods: state.myCods.concat(result.items.flat()),
                    myCodsMetadata: result.metadata
                })
            )
        );
    }

    @Action(LoadTasks)
    loadTasks(ctx: StateContext<CodOverviewStateModel>) {
        const state = ctx.getState();
        const filters = state.filters;

        ctx.patchState({ isLoadingTasks: true });
        return this.codService.getMyTasks(0, PAGE_SIZE, filters).pipe(
            catchError(() => {
                ctx.patchState({ isLoadingTasks: false });
                return of(void 0);
            }),
            filter(response => !!response),
            tap(response =>
                ctx.patchState({
                    tasks: response.items.flat(),
                    tasksMetadata: response.metadata,
                    isLazyLoadingTasks: false,
                    isLoadingTasks: false
                })
            )
        );
    }

    @Action(LazyLoadTasks)
    lazyLoadTasks(ctx: StateContext<CodOverviewStateModel>): Observable<Page<GeneralCod>> {
        const state = ctx.getState();
        const filters = state.filters;

        const page = Math.round(ctx.getState().tasks.length / PAGE_SIZE);
        ctx.patchState({ isLazyLoadingTasks: true });

        return this.codService.getMyTasks(page, PAGE_SIZE, filters).pipe(
            catchError(() => {
                ctx.patchState({ isLazyLoadingTasks: false });
                return of({ items: [], metadata: {} } as unknown as Page<GeneralCod>);
            }),
            tap(result =>
                ctx.patchState({
                    tasks: state.tasks.concat(result.items.flat()),
                    tasksMetadata: result.metadata,
                    isLazyLoadingTasks: false
                })
            )
        );
    }

    @Action(SetFilter)
    setFilter(ctx: StateContext<CodOverviewStateModel>, { filter }: SetFilter) {
        const state = ctx.getState();
        ctx.patchState({ filters: { ...state.filters, ...filter } });
    }

    @Action(DeleteCod)
    deleteCod(ctx: StateContext<CodOverviewStateModel>, action: DeleteCod): Observable<void> {
        const id = action.id;

        ctx.patchState({ myCods: ctx.getState().myCods.filter(cod => cod.id !== id) });

        return this.codService.deleteCod(id);
    }
}
